/**
 * The way back from editing a term at its source: the bar at the foot of the
 * screen that says what to do there, then that it was saved, and offers
 * "Return to [page]"; and the question before a move the panel makes would
 * drop unsaved work.
 *
 * The page returned to is the one the attorney was on when they first used the
 * panel, and it holds across further panel actions until the trip ends. The
 * save is noticed in the terms themselves: once the term's served words differ
 * from what they were when it was opened, the bar says so.
 */
import { useState, useSyncExternalStore } from "react";
import { useLocation, useNavigate } from "react-router";
import { Check, Pencil, X } from "lucide-react";
import type {
  Term,
  TermGroupKind,
  TermsResponse,
} from "@patent-studio/api-types";
import { Button, IconButton } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Panel } from "@/components/ui/Panel";
import { cn } from "@/lib/utils";
import { hasUnsavedDraft } from "@/hooks/use-unsaved-draft";
import { termDestination, type TermTarget } from "./term-route";
import { wordsOf } from "./terms-seen";
import { SURFACE } from "./TermEntries";

/** The term the panel sent the attorney to, as it read when they went. */
interface Opened {
  kind: TermGroupKind;
  thingId: string;
  name: string;
  words: string;
  edit: boolean;
}

/** Where the trip started: the page, and its name for the Return button. */
interface Origin {
  path: string;
  label: string;
}

export interface ReturnTrip {
  /** Open a term's record where it was defined, asking first if a draft would be lost. */
  go: (kind: TermGroupKind, term: Term, edit: boolean) => void;
  opened: Opened | null;
  origin: Origin | null;
  /** The term's name once its words have changed since it was opened; nothing before. */
  savedAs: string | null;
  returnTo: () => void;
  dismiss: () => void;
  /** The move waiting on "Leave without saving?", and where it goes. */
  asking: { dest: string; leave: () => void } | null;
  answer: (leave: boolean) => void;
}

function termIn(
  view: TermsResponse | undefined,
  kind: TermGroupKind,
  thingId: string,
): Term | undefined {
  return view?.groups
    .find((group) => group.kind === kind)
    ?.terms.find((term) => term.thing_id === thingId);
}

/** The trip's state for the shell, for the draft open and the page the attorney is on. */
export function useReturnTrip(
  taskId: string,
  view: TermsResponse | undefined,
  here: string,
): ReturnTrip {
  const navigate = useNavigate();
  const location = useLocation();
  const [opened, setOpened] = useState<Opened | null>(null);
  const [origin, setOrigin] = useState<Origin | null>(null);
  const [asking, setAsking] = useState<ReturnTrip["asking"]>(null);

  const ask = (dest: string, leave: () => void) => {
    if (hasUnsavedDraft()) setAsking({ dest, leave });
    else leave();
  };
  const end = () => {
    setOpened(null);
    setOrigin(null);
  };
  const now =
    opened === null ? undefined : termIn(view, opened.kind, opened.thingId);
  return {
    go: (kind, term, edit) => {
      ask(SURFACE[kind], () => {
        setOrigin(
          (held) =>
            held ?? { path: location.pathname + location.search, label: here },
        );
        setOpened({
          kind,
          thingId: term.thing_id,
          name: term.name,
          words: wordsOf(term),
          edit,
        });
        const target: TermTarget = { recordId: term.source.record_id, edit };
        void navigate(termDestination(taskId, kind), {
          state: { term: target },
        });
      });
    },
    opened,
    origin,
    savedAs:
      opened !== null && now !== undefined && wordsOf(now) !== opened.words
        ? now.name
        : null,
    returnTo: () => {
      if (origin === null) return;
      ask(origin.label, () => {
        end();
        // Said to the page returned to, so a review keeps what it showed before the trip.
        void navigate(origin.path, { state: { fromTermTrip: true } });
      });
    },
    dismiss: end,
    asking,
    answer: (leave) => {
      const waiting = asking;
      setAsking(null);
      if (leave) waiting?.leave();
    },
  };
}

/** The bar's height off the window's foot when nothing is pinned there: clear of the notice row. */
const BAR_FLOOR = 56;
/** The space the bar keeps above a bar pinned to the column's foot. */
const ABOVE_PINNED = 14;

/**
 * How far off the window's foot the bar sits: above the page's pinned bar where
 * it has one, as the design docks it, else clear of the notice row.
 */
function barBottom(): number {
  const top = document
    .querySelector("[data-pinned]")
    ?.getBoundingClientRect().top;
  return top === undefined || top <= 0
    ? BAR_FLOOR
    : Math.max(BAR_FLOOR, Math.round(window.innerHeight - top + ABOVE_PINNED));
}

/** The page moving under the bar: a scroll of any column, or the window resizing. */
function subscribeToLayout(onChange: () => void): () => void {
  window.addEventListener("resize", onChange);
  document.addEventListener("scroll", onChange, true);
  return () => {
    window.removeEventListener("resize", onChange);
    document.removeEventListener("scroll", onChange, true);
  };
}

function floorOnly(): number {
  return BAR_FLOOR;
}

/**
 * The bar at the foot of the content column while a trip is under way: what to
 * do here, then that it was saved, and the way back. Centred over the column,
 * clear of the dock when the Terminology panel takes its track.
 */
export function ReturnBar({
  trip,
  docked,
}: {
  trip: ReturnTrip;
  docked: boolean;
}) {
  const { opened, origin } = trip;
  const bottom = useSyncExternalStore(subscribeToLayout, barBottom, floorOnly);
  if (opened === null || origin === null) return null;
  const saved = trip.savedAs !== null;
  return (
    <Panel
      role="status"
      pad="none"
      // The design's bar: the brand's deep violet, over whatever page it floats on.
      className={cn(
        "fixed left-[calc(224px+(100vw-224px)/2)] z-50 flex w-max max-w-[min(640px,90vw)] -translate-x-1/2 items-center gap-2.5 rounded-[14px] border-primary bg-primary py-2 pr-2.5 pl-3 text-[12.5px] leading-snug text-white shadow-[0_10px_30px_rgba(15,0,27,0.28)]",
        docked && "dock:left-[calc(224px+(100vw-224px-375px)/2)]",
      )}
      style={{ bottom }}
    >
      <span
        aria-hidden
        className={cn(
          "grid size-6 flex-none place-items-center rounded-full [&_svg]:size-[13px]",
          saved ? "bg-success text-white" : "bg-white/15",
        )}
      >
        {saved ? <Check /> : <Pencil />}
      </span>
      <span className="min-w-0 flex-1 text-pretty">
        {saved ? (
          <>
            <b>Saved.</b> The terminology table now shows "{trip.savedAs}".
          </>
        ) : opened.edit ? (
          `Edit "${opened.name}" here and save it.`
        ) : (
          `Showing "${opened.name}".`
        )}
      </span>
      <Button variant="secondary" size="dense" onClick={trip.returnTo}>
        Return to {origin.label}
      </Button>
      <IconButton
        variant="quiet"
        size="dense"
        aria-label="Dismiss"
        title="Dismiss"
        className="text-white/70 hover:bg-white/15 hover:text-white"
        onClick={trip.dismiss}
      >
        <X />
      </IconButton>
    </Panel>
  );
}

/** "Leave without saving?", before a move the panel makes would drop an unsaved draft. */
export function LeaveQuestion({ trip }: { trip: ReturnTrip }) {
  return (
    <ConfirmDialog
      open={trip.asking !== null}
      title="Leave without saving?"
      description={`You have unsaved changes on this page. If you go to ${trip.asking?.dest ?? ""} now, they will be lost.`}
      actionLabel="Leave Page"
      destructive
      onCancel={() => {
        trip.answer(false);
      }}
      onConfirm={() => {
        trip.answer(true);
      }}
    />
  );
}

/** Whether a page was reached by the Return bar, from editing a term at its source. */
export function cameBackFromTrip(state: unknown): boolean {
  return (
    typeof state === "object" &&
    state !== null &&
    "fromTermTrip" in state &&
    state.fromTermTrip === true
  );
}
